import { z } from 'zod';
import {
    CardPointeTerminalSaleOutcome,
    type CardPointeTerminalSaleAuthorizationBody,
    type CardPointeTerminalSaleErrorBody,
    type CardPointeTerminalSaleResponseDetails,
} from '../../../types/ipg/cardpointe/cardpointe-terminal-sale-response.types';

type CardPointeTerminalSaleApprovedAuthorizationBody = CardPointeTerminalSaleAuthorizationBody & {
    readonly respstat: 'A';
    readonly retref: string;
    readonly amount: string;
    readonly merchid: string;
};

type CardPointeTerminalSaleDeclinedAuthorizationBody = CardPointeTerminalSaleAuthorizationBody & {
    readonly respstat: 'C';
    readonly resptext: string;
};

export const CardPointeTerminalSaleAuthorizationBodySchema: z.ZodObject<{
    respstat: z.ZodUnion<readonly [z.ZodLiteral<'A'>, z.ZodLiteral<'C'>]>;
    amount: z.ZodOptional<z.ZodString>;
    resptext: z.ZodOptional<z.ZodString>;
    authcode: z.ZodOptional<z.ZodString>;
    respcode: z.ZodOptional<z.ZodString>;
    merchid: z.ZodOptional<z.ZodString>;
    cvvresp: z.ZodOptional<z.ZodString>;
    retref: z.ZodOptional<z.ZodString>;
    avsresp: z.ZodOptional<z.ZodString>;
    respproc: z.ZodOptional<z.ZodString>;
    batchid: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    account: z.ZodOptional<z.ZodString>;
    expiry: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
    binInfo: z.ZodOptional<z.ZodUnknown>;
    emvTagData: z.ZodOptional<z.ZodUnknown>;
    tokenExpired: z.ZodOptional<z.ZodBoolean>;
    entrymode: z.ZodOptional<z.ZodString>;
    bintype: z.ZodOptional<z.ZodString>;
    orderId: z.ZodOptional<z.ZodString>;
    receiptData: z.ZodOptional<z.ZodUnknown>;
    fee_amount: z.ZodOptional<z.ZodString>;
    fee_value: z.ZodOptional<z.ZodString>;
    fee_type: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodLiteral<'SURCHRG'>, z.ZodLiteral<'SURCHRG_WAIVED'>]>
    >;
    fee_format: z.ZodOptional<z.ZodUnion<readonly [z.ZodLiteral<'percent'>, z.ZodLiteral<''>]>>;
    fee_authcode: z.ZodOptional<z.ZodString>;
    fee_merchid: z.ZodOptional<z.ZodString>;
    fee_retref: z.ZodOptional<z.ZodString>;
}> = z.object({
    respstat: z.union([z.literal('A'), z.literal('C')]),
    amount: z.string().min(1).optional(),
    resptext: z.string().min(1).optional(),
    authcode: z.string().min(1).optional(),
    respcode: z.string().min(1).optional(),
    merchid: z.string().min(1).optional(),
    cvvresp: z.string().min(1).optional(),
    retref: z.string().min(1).optional(),
    avsresp: z.string().min(1).optional(),
    respproc: z.string().min(1).optional(),
    batchid: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    account: z.string().min(1).optional(),
    expiry: z.string().min(1).optional(),
    name: z.string().optional(),
    binInfo: z.unknown().optional(),
    emvTagData: z.unknown().optional(),
    tokenExpired: z.boolean().optional(),
    entrymode: z.string().min(1).optional(),
    bintype: z.string().optional(),
    orderId: z.string().min(1).optional(),
    receiptData: z.unknown().optional(),
    fee_amount: z
        .string()
        .regex(/^\d+(?:\.\d{2})?$/)
        .optional(),
    fee_value: z.string().regex(/^\d+$/).optional(),
    fee_type: z.union([z.literal('SURCHRG'), z.literal('SURCHRG_WAIVED')]).optional(),
    fee_format: z.union([z.literal('percent'), z.literal('')]).optional(),
    fee_authcode: z.string().optional(),
    fee_merchid: z.string().optional(),
    fee_retref: z.string().optional(),
}) satisfies z.ZodType<CardPointeTerminalSaleAuthorizationBody>;

export const CardPointeTerminalSaleErrorBodySchema: z.ZodObject<{
    errorCode: z.ZodString;
    errorMessage: z.ZodString;
    authResponse: z.ZodOptional<typeof CardPointeTerminalSaleAuthorizationBodySchema>;
}> = z.object({
    errorCode: z.string().min(1),
    errorMessage: z.string().min(1),
    authResponse: CardPointeTerminalSaleAuthorizationBodySchema.optional(),
}) satisfies z.ZodType<CardPointeTerminalSaleErrorBody>;

export const CardPointeTerminalSaleApprovedAuthorizationBodySchema: z.ZodType<CardPointeTerminalSaleApprovedAuthorizationBody> =
    CardPointeTerminalSaleAuthorizationBodySchema.extend({
        respstat: z.literal('A'),
        retref: z.string().min(1),
        amount: z.string().min(1),
        merchid: z.string().min(1),
    });

export const CardPointeTerminalSaleDeclinedAuthorizationBodySchema: z.ZodType<CardPointeTerminalSaleDeclinedAuthorizationBody> =
    CardPointeTerminalSaleAuthorizationBodySchema.extend({
        respstat: z.literal('C'),
        resptext: z.string().min(1),
    });

export const CardPointeTerminalSaleApprovedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalSaleOutcome.APPROVED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeTerminalSaleApprovedAuthorizationBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalSaleOutcome.APPROVED),
    httpStatus: z.literal(200),
    body: CardPointeTerminalSaleApprovedAuthorizationBodySchema,
});

export const CardPointeTerminalSaleDeclinedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalSaleOutcome.DECLINED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeTerminalSaleDeclinedAuthorizationBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalSaleOutcome.DECLINED),
    httpStatus: z.literal(200),
    body: CardPointeTerminalSaleDeclinedAuthorizationBodySchema,
});

export const CardPointeTerminalSaleCanceledResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalSaleOutcome.CANCELED>;
    httpStatus: z.ZodNumber;
    body: typeof CardPointeTerminalSaleErrorBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalSaleOutcome.CANCELED),
    httpStatus: z.number().int().positive(),
    body: CardPointeTerminalSaleErrorBodySchema,
});

export const CardPointeTerminalSaleMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalSaleOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CardPointeTerminalSaleOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
});

export const CardPointeTerminalSaleResponseDetailsSchema: z.ZodType<CardPointeTerminalSaleResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeTerminalSaleApprovedResponseDetailsSchema,
        CardPointeTerminalSaleDeclinedResponseDetailsSchema,
        CardPointeTerminalSaleCanceledResponseDetailsSchema,
        CardPointeTerminalSaleMalformedResponseDetailsSchema,
    ]);
